import { useState } from "react";
import { BrandLockup } from "@/components/layout/BrandLockup";
import { useNavigate, Link } from "react-router-dom";
import { useAuthActions } from "@/lib/auth/useAuth";
import { toast } from "@/components/Toast/useToast";
import { track } from "@/lib/monitoring/posthog";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

// Beta allowlist — set VITE_BETA_EMAILS in Netlify as a comma-separated list.
// If the env var is not set, signup is open to everyone.
const BETA_EMAILS: Set<string> | null = (() => {
  const raw = import.meta.env.VITE_BETA_EMAILS as string | undefined;
  if (!raw) return null;
  return new Set(
    raw
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean),
  );
})();

// ─── Supabase error → human-readable message ──────────────────────────────────
function mapSignupError(raw: string): {
  field: "email" | "password" | "form";
  message: string;
} {
  const msg = raw.toLowerCase();
  if (
    msg.includes("user already registered") ||
    msg.includes("already been registered") ||
    msg.includes("email address is already")
  ) {
    return {
      field: "form",
      message: "Unable to create account. Please try again or use a different email.",
    };
  }
  if (msg.includes("invalid email")) {
    return { field: "email", message: "Please enter a valid email address." };
  }
  if (
    msg.includes("password should be at least") ||
    msg.includes("password is too short")
  ) {
    return {
      field: "password",
      message: "Password must be at least 8 characters.",
    };
  }
  if (msg.includes("too many requests") || msg.includes("rate limit")) {
    return {
      field: "form",
      message: "Too many attempts. Please wait a moment and try again.",
    };
  }
  return { field: "form", message: raw };
}

type FieldErrors = {
  email?: string;
  password?: string;
  confirmPassword?: string;
};

export function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuthActions();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // ── Client-side validation ───────────────────────────────────────────────────
  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!email.trim()) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (password && confirmPassword !== password) {
      errors.confirmPassword = "Passwords do not match.";
    }
    return errors;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    // CAPTCHA check — only enforced when VITE_TURNSTILE_SITE_KEY is set
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error("Please complete the security check before continuing.");
      return;
    }

    // Check beta allowlist if active
    if (BETA_EMAILS && !BETA_EMAILS.has(email.trim().toLowerCase())) {
      setFieldErrors({
        email:
          "This email is not on the beta access list. Contact support@momentumpeaks.com to request access.",
      });
      return;
    }

    setIsLoading(true);

    const result = await signup(email, password, captchaToken ?? undefined);
    setIsLoading(false);
    // Reset token so user can re-verify if they retry after an error
    setCaptchaToken(null);

    if (result.success) {
      track("signup_completed", { email });
      if (result.requiresEmailConfirmation) {
        // Supabase has email confirmation enabled — no live session yet.
        // Show the check-your-email screen instead of going to onboarding.
        setEmailSent(true);
      } else {
        navigate("/billing");
      }
    } else {
      const mapped = mapSignupError(result.error ?? "Signup failed");
      if (mapped.field === "form") {
        toast.error(mapped.message);
      } else {
        setFieldErrors({ [mapped.field]: mapped.message });
      }
    }
  };

  const fieldClass = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-lg transition-colors outline-none ${
      hasError ? "ring-2 ring-[rgba(185,28,28,0.4)]" : ""
    }`;

  const fieldStyle = (hasError: boolean): React.CSSProperties => ({
    backgroundColor: "#ffffff",
    color: "#10201d",
    border: hasError ? "1px solid #b91c1c" : "1px solid #bdc9c4",
  });

  const fieldFocusHandler = (hasError: boolean) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.style.borderColor = hasError ? "#b91c1c" : "#006b5f";
      e.target.style.boxShadow = hasError
        ? "0 0 0 2px rgba(185,28,28,0.4)"
        : "0 0 0 2px rgba(0,107,95,0.35)";
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.style.borderColor = hasError ? "#b91c1c" : "#bdc9c4";
      e.target.style.boxShadow = "none";
    },
  });

  const isButtonDisabled = isLoading || (!!TURNSTILE_SITE_KEY && !captchaToken);

  // ── Email confirmation screen ─────────────────────────────────────────────
  if (emailSent) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: "#f3f5f2" }}
      >
        <div className="max-w-md w-full text-center">
          <div className="mb-6 flex justify-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "rgba(0, 107, 95, 0.1)" }}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: "#006b5f" }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <h1
            className="text-3xl font-bold mb-3"
            style={{ color: "#10201d" }}
          >
            Check your email
          </h1>
          <p className="mb-2" style={{ color: "#52645f" }}>
            We've sent a confirmation link to
          </p>
          <p className="font-medium mb-6" style={{ color: "#10201d" }}>{email}</p>
          <p className="text-sm mb-8" style={{ color: "#60716c" }}>
            Click the link in the email to confirm your account, then come back
            here to sign in.
          </p>
          <Link
            to="/login"
            className="inline-block w-full px-4 py-3 font-bold rounded-lg transition-all text-center"
            style={{ backgroundColor: "#006b5f", color: "#ffffff" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#00574e";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#006b5f";
            }}
          >
            Go to Sign In
          </Link>
          <p className="mt-4 text-sm" style={{ color: "#60716c" }}>
            Didn't receive it?{" "}
            <button
              onClick={() => setEmailSent(false)}
              className="hover:underline"
              style={{ color: "#006b5f" }}
            >
              Try again
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <main
      id="main-content"
      className="flex min-h-[100dvh] overflow-x-hidden overflow-y-auto"
      style={{ backgroundColor: "#f3f5f2" }}
    >
      {/* Left Side - Form */}
      <div className="flex min-w-0 flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-12">
        <div className="max-w-md w-full">
          <div className="mb-8 sm:mb-12">
            <Link
              to="/"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <BrandLockup size="xl" />
            </Link>
          </div>

          <div className="mb-8">
            <h1
              className="text-3xl sm:text-4xl font-bold mb-2"
              style={{ color: "#10201d" }}
            >
              Start Training Smarter
            </h1>
            <p style={{ color: "#52645f" }}>Create your account to get started</p>
          </div>

          <div
            className="rounded-lg p-8"
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #bdc9c4",
            }}
          >
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: "#344844" }}
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email)
                      setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  className={fieldClass(!!fieldErrors.email)}
                  style={{
                    ...fieldStyle(!!fieldErrors.email),
                    ...(isLoading ? { opacity: 0.6 } : {}),
                  }}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? "signup-email-error" : "signup-email-hint"}
                  {...fieldFocusHandler(!!fieldErrors.email)}
                />
                {fieldErrors.email && (
                  <p
                    id="signup-email-error"
                    role="alert"
                    className="mt-1.5 text-xs flex items-center gap-1"
                    style={{ color: "#b91c1c" }}
                  >
                    <svg
                      className="w-3 h-3 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-2"
                  style={{ color: "#344844" }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password)
                      setFieldErrors((prev) => ({
                        ...prev,
                        password: undefined,
                      }));
                  }}
                  className={fieldClass(!!fieldErrors.password)}
                  style={{
                    ...fieldStyle(!!fieldErrors.password),
                    ...(isLoading ? { opacity: 0.6 } : {}),
                  }}
                  placeholder="••••••••"
                  disabled={isLoading}
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? "signup-password-error" : "signup-password-hint"}
                  {...fieldFocusHandler(!!fieldErrors.password)}
                />
                {fieldErrors.password ? (
                  <p
                    id="signup-password-error"
                    role="alert"
                    className="mt-1.5 text-xs flex items-center gap-1"
                    style={{ color: "#b91c1c" }}
                  >
                    <svg
                      className="w-3 h-3 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {fieldErrors.password}
                  </p>
                ) : (
                  <p
                    id="signup-password-hint"
                    className="mt-1.5 text-xs"
                    style={{ color: "#60716c" }}
                  >
                    Minimum 8 characters.
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium mb-2"
                  style={{ color: "#344844" }}
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword)
                      setFieldErrors((prev) => ({
                        ...prev,
                        confirmPassword: undefined,
                      }));
                  }}
                  className={fieldClass(!!fieldErrors.confirmPassword)}
                  style={{
                    ...fieldStyle(!!fieldErrors.confirmPassword),
                    ...(isLoading ? { opacity: 0.6 } : {}),
                  }}
                  placeholder="••••••••"
                  disabled={isLoading}
                  aria-invalid={!!fieldErrors.confirmPassword}
                  aria-describedby={fieldErrors.confirmPassword ? "signup-confirm-error" : undefined}
                  {...fieldFocusHandler(!!fieldErrors.confirmPassword)}
                />
                {fieldErrors.confirmPassword && (
                  <p
                    id="signup-confirm-error"
                    role="alert"
                    className="mt-1.5 text-xs flex items-center gap-1"
                    style={{ color: "#b91c1c" }}
                  >
                    <svg
                      className="w-3 h-3 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>

              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                    options={{ theme: "light" }}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isButtonDisabled}
                className="w-full px-4 py-3 font-bold rounded-lg transition-all"
                style={{
                  backgroundColor: isButtonDisabled ? "#bdc9c4" : "#006b5f",
                  color: isButtonDisabled ? "#8a9b95" : "#ffffff",
                  cursor: isButtonDisabled ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isButtonDisabled) e.currentTarget.style.backgroundColor = "#00574e";
                }}
                onMouseLeave={(e) => {
                  if (!isButtonDisabled) e.currentTarget.style.backgroundColor = "#006b5f";
                }}
              >
                {isLoading ? "Creating account..." : "Sign Up"}
              </button>
            </form>

            <p
              className="mt-4 text-center text-xs"
              style={{ color: "#60716c" }}
            >
              By signing up you agree to our{" "}
              <Link
                to="/terms"
                className="underline hover:opacity-80"
                style={{ color: "#60716c" }}
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy-policy"
                className="underline hover:opacity-80"
                style={{ color: "#60716c" }}
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div
              className="mt-4 text-center text-sm"
              style={{ color: "#52645f" }}
            >
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-medium hover:underline"
                style={{ color: "#006b5f" }}
              >
                Log in
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block flex-1 relative">
        <div
          className="absolute inset-0 z-10"
          style={{
            background: "linear-gradient(to bottom right, rgba(16,32,29,0.55), rgba(16,32,29,0.55))",
          }}
        ></div>
        <img
          src="https://images.unsplash.com/photo-1628127050562-3049ead43a35?w=1200&q=80"
          alt="start your journey"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: "#ffffff" }}
          >
            Join Thousands of Athletes
          </h2>
          <p
            className="text-lg"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            Professional training plans exported directly to your device
          </p>
        </div>
      </div>
    </main>
  );
}
